import { applySharedAccessibilityDomState } from './shared'
import type { AccessibilityFontSize, AccessibilityLineSpacing } from './shared'

export interface UserAccessibilitySettings {
  fontSize: AccessibilityFontSize
  highContrast: boolean
  reduceMotion: boolean
  dyslexiaFont: boolean
  screenReaderOptimized: boolean
  lineSpacing: AccessibilityLineSpacing
  showAccessibilityButton: boolean
  showTTSToolbar: boolean
}

const COOKIE_NAME = 'lina-accessibility-settings'
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60

const DEFAULT_SETTINGS: UserAccessibilitySettings = {
  fontSize: 'medium',
  highContrast: false,
  reduceMotion: false,
  dyslexiaFont: false,
  screenReaderOptimized: false,
  lineSpacing: 'normal',
  showAccessibilityButton: true,
  showTTSToolbar: false,
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null

  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null
  }

  return null
}

function setCookie(name: string, value: string, maxAge: number): void {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=${value}; max-age=${maxAge}; path=/; SameSite=Lax`
}

export function getUserAccessibilitySettings(): UserAccessibilitySettings {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS

  try {
    const stored = getCookie(COOKIE_NAME)
    if (stored) {
      const decoded = decodeURIComponent(stored)
      return { ...DEFAULT_SETTINGS, ...JSON.parse(decoded) }
    }
  } catch (error) {
    console.error('Failed to load user accessibility settings:', error)
  }

  return DEFAULT_SETTINGS
}

export function saveUserAccessibilitySettings(settings: Partial<UserAccessibilitySettings>): void {
  if (typeof window === 'undefined') return

  try {
    const current = getUserAccessibilitySettings()
    const updated = { ...current, ...settings }
    const encoded = encodeURIComponent(JSON.stringify(updated))
    setCookie(COOKIE_NAME, encoded, COOKIE_MAX_AGE)
  } catch (error) {
    console.error('Failed to save user accessibility settings:', error)
  }
}

export function applyUserAccessibilitySettings(settings: UserAccessibilitySettings): void {
  applySharedAccessibilityDomState(settings, { increaseDyslexiaFontSizePercent: 5 })
}

export function resetUserAccessibilitySettings(): void {
  saveUserAccessibilitySettings(DEFAULT_SETTINGS)
  applyUserAccessibilitySettings(DEFAULT_SETTINGS)
}

export function initializeUserAccessibilitySettings(): void {
  const settings = getUserAccessibilitySettings()
  applyUserAccessibilitySettings(settings)
}
